import { useState, useEffect, createContext, useContext, useCallback, useMemo } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { api } from "./lib/api";
import Header from "./components/Header";
import BottomNav from "./components/BottomNav";
import CountdownBanner from "./components/CountdownBanner";
import ForcePasswordReset from "./components/ForcePasswordReset";
import Leaderboard from "./pages/Leaderboard";
import HowItWorks from "./pages/HowItWorks";
import Feed from "./pages/Feed";
import Reels from "./pages/Reels";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Judge from "./pages/Judge";
import Admin from "./pages/Admin";
import Portfolio from "./pages/Portfolio";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const TITLES = {
  "/": "Leaderboard", "/how-it-works": "How it works", "/feed": "The Feed", "/reels": "Reels",
  "/login": "Sign in", "/register": "Register", "/reset-password": "Reset password",
  "/dashboard": "My Property", "/judge": "Judging", "/portfolio": "My Portfolio", "/admin": "Admin",
};

function TitleUpdater() {
  const { pathname } = useLocation();
  useEffect(() => {
    const t = TITLES[pathname];
    document.title = t ? `${t} · Site Wars` : "Site Wars · Redstone Residential";
  }, [pathname]);
  return null;
}

const PROPERTY_ROLES = ["property_user"];
const JUDGE_ROLES = ["judge", "admin"];
const ADMIN_ROLES = ["admin"];

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-12 text-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRegional, setIsRegional] = useState(false);

  const refresh = useCallback(async () => {
    localStorage.removeItem("sw_token");
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!user || user.role === "admin") { setIsRegional(false); return; }
    api.get("/regionals/summary").then(({ data }) => {
      const name = (user.name || "").trim().toLowerCase();
      setIsRegional(data.some((r) => r.regional.toLowerCase() === name));
    }).catch(() => setIsRegional(false));
  }, [user]);

  const signIn = useCallback((payload) => {
    setUser(payload.user);
  }, []);
  const signOut = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.warn("Logout request failed (clearing session locally):", error?.message);
    }
    setUser(null);
  }, []);

  const authValue = useMemo(() => ({ user, setUser, loading, signIn, signOut, isRegional }), [user, loading, signIn, signOut, isRegional]);

  return (
    <AuthContext.Provider value={authValue}>
      <BrowserRouter>
        <TitleUpdater />
        <div className="min-h-screen bg-white flex flex-col pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:pb-0">
          <Header />
          <CountdownBanner />
          {user?.must_reset_password && <ForcePasswordReset />}
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<Leaderboard />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/feed" element={<Protected><Feed /></Protected>} />
              <Route path="/reels" element={<Reels />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/dashboard" element={<Protected roles={PROPERTY_ROLES}><Dashboard /></Protected>} />
              <Route path="/judge" element={<Protected roles={JUDGE_ROLES}><Judge /></Protected>} />
              <Route path="/portfolio" element={<Protected><Portfolio /></Protected>} />
              <Route path="/admin" element={<Protected roles={ADMIN_ROLES}><Admin /></Protected>} />
            </Routes>
          </main>
          <footer className="sw-navy text-slate-400 text-xs py-5 text-center">
            Site Wars · Redstone Residential · July 2026 · <Link to="/how-it-works" className="text-slate-300 underline underline-offset-2 hover:text-white transition-colors" data-testid="footer-how-it-works">How it works</Link>
          </footer>
          <BottomNav />
        </div>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
